/**
 * TQL Evaluator - Evaluate TQL expressions against HTTP requests
 */

import type {
  TqlAstNode,
  TqlFieldExpression,
  TqlHeaderExpression,
  TqlPresetReference,
  TqlLogicalExpression,
  TqlNotExpression,
  TqlGroupExpression,
  FilterPreset,
} from './types';
import { parseTql } from './parser';

// Request context for evaluation
export type HttpRequestContext = {
  id: string;
  sequentialId?: number;
  method: string;
  url: string;
  host?: string;
  path?: string;
  query?: string;
  ext?: string;
  port?: number;
  headers: Record<string, string | string[]>;
  body?: string;
  rawRequest?: string;
  timestamp: number | Date;
  status?: number;
  responseHeaders?: Record<string, string | string[]>;
  responseMime?: string;
  responseBody?: string;
  rawResponse?: string;
  length?: number;
  roundtrip?: number;
  comment?: string;
  tags?: string[];
  highlight?: string | null;
};

// Preset resolver function type
export type PresetResolver = (name: string) => FilterPreset | undefined;

/**
 * Evaluate an TQL AST against a request
 */
export function evaluateTql(
  ast: TqlAstNode,
  request: HttpRequestContext,
  resolvePreset?: PresetResolver,
): boolean {
  switch (ast.type) {
    case 'field':
      return evaluateFieldExpression(ast, request);
    case 'header':
      return evaluateHeaderExpression(ast, request);
    case 'preset':
      return evaluatePresetReference(ast, request, resolvePreset);
    case 'logical':
      return evaluateLogicalExpression(ast, request, resolvePreset);
    case 'not':
      return evaluateNotExpression(ast, request, resolvePreset);
    case 'group':
      return evaluateGroupExpression(ast, request, resolvePreset);
    default:
      return true;
  }
}

function evaluateFieldExpression(
  expr: TqlFieldExpression,
  request: HttpRequestContext,
): boolean {
  const value = getFieldValue(expr.field, request);
  return evaluateOperator(expr.operator, value, expr.value);
}

function evaluateHeaderExpression(
  expr: TqlHeaderExpression,
  request: HttpRequestContext,
): boolean {
  const headers =
    expr.target === 'req' ? request.headers : request.responseHeaders;
  // Note: 'resp' target also uses responseHeaders
  if (!headers) {
    return expr.operator === 'nexists';
  }

  // Case-insensitive header lookup
  const headerKey = Object.keys(headers).find(
    (key) => key.toLowerCase() === expr.headerName.toLowerCase(),
  );
  const headerValue = headerKey ? headers[headerKey] : undefined;

  if (expr.operator === 'exists') {
    return headerValue !== undefined;
  }
  if (expr.operator === 'nexists') {
    return headerValue === undefined;
  }

  const stringValue = Array.isArray(headerValue)
    ? headerValue.join(', ')
    : headerValue || '';

  return evaluateOperator(expr.operator, stringValue, expr.value);
}

function evaluatePresetReference(
  expr: TqlPresetReference,
  request: HttpRequestContext,
  resolvePreset?: PresetResolver,
): boolean {
  if (!resolvePreset) {
    console.warn(
      `Preset "${expr.presetName}" cannot be resolved - no resolver provided`,
    );
    return true;
  }

  const preset = resolvePreset(expr.presetName);
  if (!preset) {
    console.warn(`Preset "${expr.presetName}" not found`);
    return true;
  }

  // Parse and evaluate the preset's query
  const parseResult = parseTql(preset.query);
  if (!parseResult.success) {
    console.warn(
      `Failed to parse preset "${expr.presetName}": ${parseResult.error}`,
    );
    return true;
  }

  return evaluateTql(parseResult.ast, request, resolvePreset);
}

function evaluateLogicalExpression(
  expr: TqlLogicalExpression,
  request: HttpRequestContext,
  resolvePreset?: PresetResolver,
): boolean {
  const left = evaluateTql(expr.left, request, resolvePreset);

  if (expr.operator === 'AND') {
    // Short-circuit: if left is false, don't evaluate right
    if (!left) return false;
    return evaluateTql(expr.right, request, resolvePreset);
  }

  // OR operator
  // Short-circuit: if left is true, don't evaluate right
  if (left) return true;
  return evaluateTql(expr.right, request, resolvePreset);
}

function evaluateNotExpression(
  expr: TqlNotExpression,
  request: HttpRequestContext,
  resolvePreset?: PresetResolver,
): boolean {
  return !evaluateTql(expr.expression, request, resolvePreset);
}

function evaluateGroupExpression(
  expr: TqlGroupExpression,
  request: HttpRequestContext,
  resolvePreset?: PresetResolver,
): boolean {
  return evaluateTql(expr.expression, request, resolvePreset);
}

function getFieldValue(
  field: string,
  request: HttpRequestContext,
): string | number | undefined {
  switch (field) {
    // Request fields
    case 'req.method':
      return request.method?.toUpperCase();
    case 'req.path':
      return request.path ?? getPathFromUrl(request.url);
    case 'req.query':
      return request.query ?? getQueryFromUrl(request.url);
    case 'req.host':
      return request.host || getHostFromUrl(request.url);
    case 'req.body':
      return request.body;
    case 'req.raw':
      return request.rawRequest;
    case 'req.ext':
      return request.ext ?? getExtensionFromUrl(request.url);
    case 'req.port':
      return request.port ?? getPortFromUrl(request.url);

    // Response fields
    case 'resp.code':
      return request.status;
    case 'resp.body':
      return request.responseBody;
    case 'resp.raw':
      return request.rawResponse;
    case 'resp.roundtrip':
      return request.roundtrip;
    case 'resp.length':
      return request.length;
    case 'resp.mime':
      return request.responseMime ?? getMimeType(request.responseHeaders);

    // Metadata
    case 'id':
      return request.id;
    case 'timestamp':
      return typeof request.timestamp === 'number'
        ? request.timestamp
        : request.timestamp?.getTime();
    case 'comment':
      return request.comment;
    case 'tag':
      return request.tags?.join(',');
    case 'highlight':
      return request.highlight ?? undefined;

    default:
      return undefined;
  }
}

function getQueryFromUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.search.substring(1);
  } catch {
    const queryStart = url.indexOf('?');
    if (queryStart === -1) return '';
    return url.substring(queryStart + 1);
  }
}

function evaluateOperator(
  operator: string,
  fieldValue: string | number | undefined,
  compareValue?: string | number | boolean,
): boolean {
  // Handle existence operators
  if (operator === 'exists') {
    return fieldValue !== undefined && fieldValue !== '' && fieldValue !== null;
  }
  if (operator === 'nexists') {
    return fieldValue === undefined || fieldValue === '' || fieldValue === null;
  }

  // For other operators, undefined field means no match (except neq/ncont)
  if (fieldValue === undefined) {
    return operator === 'neq' || operator === 'ncont';
  }

  // Convert to strings for string operators
  const fieldStr = String(fieldValue).toLowerCase();
  const compareStr = String(compareValue ?? '').toLowerCase();

  switch (operator) {
    // String operators
    case 'eq':
      if (typeof fieldValue === 'number' && typeof compareValue === 'number') {
        return fieldValue === compareValue;
      }
      return fieldStr === compareStr;

    case 'neq':
      if (typeof fieldValue === 'number' && typeof compareValue === 'number') {
        return fieldValue !== compareValue;
      }
      return fieldStr !== compareStr;

    case 'cont':
      return fieldStr.includes(compareStr);

    case 'ncont':
      return !fieldStr.includes(compareStr);

    case 'starts':
      return fieldStr.startsWith(compareStr);

    case 'ends':
      return fieldStr.endsWith(compareStr);

    case 'regex':
      try {
        const regex = new RegExp(String(compareValue ?? ''), 'i');
        return regex.test(String(fieldValue));
      } catch {
        return false;
      }

    // Numeric operators
    case 'gt':
      return Number(fieldValue) > Number(compareValue);

    case 'gte':
      return Number(fieldValue) >= Number(compareValue);

    case 'lt':
      return Number(fieldValue) < Number(compareValue);

    case 'lte':
      return Number(fieldValue) <= Number(compareValue);

    default:
      return true;
  }
}

// Helper functions
function getPathFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.pathname;
  } catch {
    // If URL parsing fails, try to extract path manually
    const pathMatch = url.match(/^[^:]+:\/\/[^/]+(\/[^?#]*)?/);
    return pathMatch?.[1] || url;
  }
}

function getHostFromUrl(url: string): string | undefined {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch {
    return undefined;
  }
}

function getExtensionFromUrl(url: string): string | undefined {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const lastDot = pathname.lastIndexOf('.');
    const lastSlash = pathname.lastIndexOf('/');
    if (lastDot > lastSlash && lastDot !== pathname.length - 1) {
      return pathname.substring(lastDot).toLowerCase();
    }
    return undefined;
  } catch {
    return undefined;
  }
}

function getPortFromUrl(url: string): number | undefined {
  try {
    const urlObj = new URL(url);
    if (urlObj.port) {
      return parseInt(urlObj.port, 10);
    }
    // Return default ports
    if (urlObj.protocol === 'https:') return 443;
    if (urlObj.protocol === 'http:') return 80;
    return undefined;
  } catch {
    return undefined;
  }
}

function getMimeType(
  headers?: Record<string, string | string[]>,
): string | undefined {
  if (!headers) return undefined;

  const contentType = headers['content-type'] || headers['Content-Type'];
  if (!contentType) return undefined;

  const value = Array.isArray(contentType) ? contentType[0] : contentType;
  return value.split(';')[0].trim().toLowerCase();
}

/**
 * Create a filter function from an TQL query string
 */
export function createTqlFilter(
  query: string,
  resolvePreset?: PresetResolver,
): (request: HttpRequestContext) => boolean {
  const parseResult = parseTql(query);

  if (!parseResult.success) {
    console.warn(`Failed to parse TQL query: ${parseResult.error}`);
    return () => true; // Return a pass-through filter on parse error
  }

  return (request: HttpRequestContext) =>
    evaluateTql(parseResult.ast, request, resolvePreset);
}

/**
 * Quick check if a request matches an TQL query
 */
export function matchesTql(
  query: string,
  request: HttpRequestContext,
  resolvePreset?: PresetResolver,
): boolean {
  const filter = createTqlFilter(query, resolvePreset);
  return filter(request);
}
